import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Card, Typography, message, Tooltip } from "antd";
import { CloseCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import ReactMarkdown from "react-markdown";
import jsPDF from "jspdf"; // add jspdf library for pdf generation
import "./UwFraud.css";
import { useLocation } from "react-router-dom";

const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

export default function UwFraud() {
  const [inputValue, setInputValue] = useState("");
  const [responseData, setResponseData] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const inputRef = useRef(null);
  const [inputError, setInputError] = useState("");

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputValue]);

  const isValidInput = (text) => {
    const hasInvalidChars = /[^a-zA-Z0-9\s&.,'-]/.test(text);
    const hasUnderscore = /_/.test(text);
    const hasHtmlTags = /<\/?[\w\s="'/:;#.-]+>/.test(text);
    return !hasInvalidChars && !hasUnderscore && !hasHtmlTags;
  };

  const onChangeHandler = (e) => {
    const rawVal = e.target.value;
    const inputElem = e.target;
    const selectionStart = inputElem.selectionStart;

    // Filter invalid characters (allow letters, digits, space, comma, period)
    let filteredVal = rawVal.replace(/[^a-zA-Z0-9\s,\.]/g, "");

    // Show message if length exceeds 256
    if (filteredVal.length > 256) {
      message.destroy(); // remove previous messages
      message.warning("Input cannot exceed 256 characters");
      filteredVal = filteredVal.slice(0, 256); // then trim
    }

    // Calculate how many characters removed before cursor
    let charsRemovedBeforeCursor = 0;
    for (let i = 0, j = 0; i < rawVal.length && j < filteredVal.length; i++) {
      if (rawVal[i] !== filteredVal[j]) {
        charsRemovedBeforeCursor++;
      } else {
        j++;
      }
      if (i >= selectionStart - 1) break;
    }

    setInputValue(filteredVal);
    
      // Show error if invalid chars removed
    if (filteredVal !== rawVal) {
      setInputError(
        "Invalid Input"
      );
    } else if (filteredVal.trim() === "") {
      setInputError("Input cannot be empty");
    } else {
      setInputError("");
    }

    // Restore cursor position after filtering
    setTimeout(() => {
      if (inputRef.current) {
        const newPos = selectionStart - charsRemovedBeforeCursor;
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };



  const handleSubmit = async () => {
    if (!inputValue.trim()) {
      message.destroy();
      message.warning("Please enter a query before submitting");
      return;
    }

    if (!isValidInput(inputValue)) {
      message.destroy();
      message.error("Invalid input !");
      return;
    }

    setLoading(true);
    setResponseData("");

    // Get UserId and Txnid from localStorage
    const storedUser = localStorage.getItem("AuthUser");
    let userId = "";
    let txnId = "";
    let token = "";

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        userId = parsedUser.UserId || "";
        txnId = parsedUser.Txnid || "";
        token = parsedUser.Token || "";
      } catch (err) {
        console.error("Failed to parse AuthUser from localStorage", err);
      }
    }

    const url = `${REACT_APP_BASE_URL}/api/v1.0/risk/analyze`;

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "userid": userId,
          "txnid": txnId,
          "Authorization": token,
        },
        body: JSON.stringify({ query: inputValue }),
      });

      if (!res.ok) throw new Error(`API request failed with status ${res.status}`);

      const contentType = res.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        const data = await res.json();
        setResponseData(data?.data.report || "No report available");
      } else {
        setResponseData("Something went wrong, please try again.");
      }
    } catch (err) {
      setResponseData("Error: Something went wrong, please try again.");
    } finally {
      setLoading(false);
    }
  };


  // Download responseData as PDF
  const handleDownloadPDF = () => {
    if (!responseData) {
      message.destroy()
      message.warning("No data available to download");
      return;
    }

    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const maxLineWidth = pageWidth - margin * 2;
    const lineHeight = 16;
    let y = margin;

    const baseFontSize = 10;
    doc.setFont("'Inter', sans-serif");
    doc.setFontSize(baseFontSize);

    function drawWrappedText(text, x, y, fontStyle = "normal", fontSize = baseFontSize) {
      doc.setFont("'Inter', sans-serif", fontStyle);
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(text, maxLineWidth);

      for (const line of lines) {
        if (y > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, x, y);
        y += lineHeight + (fontSize - baseFontSize);
      }
      return y;
    }

    function parseInlineStyles(text) {
      const chunks = [];
      const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          chunks.push({
            text: text.substring(lastIndex, match.index),
            style: "normal",
          });
        }

        if (match[2]) {
          chunks.push({ text: match[2], style: "bold" });
        } else if (match[3]) {
          chunks.push({ text: match[3], style: "italic" });
        }

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        chunks.push({
          text: text.substring(lastIndex),
          style: "normal",
        });
      }

      return chunks;
    }

    function drawStyledLine(y, xStart, maxWidth, chunks, fontSize = baseFontSize, lineHeight = 16) {
      let x = xStart;
      const spaceWidth = doc.getTextWidth(" ");

      for (const chunk of chunks) {
        doc.setFont("'Inter', sans-serif", chunk.style);
        doc.setFontSize(fontSize);

        const words = chunk.text.split(" ");

        for (let i = 0; i < words.length; i++) {
          const word = words[i];
          const wordWidth = doc.getTextWidth(word);

          if (x + wordWidth > maxWidth) {
            y += lineHeight;
            if (y > pageHeight - margin) {
              doc.addPage();
              y = margin;
            }
            x = xStart;
          }

          doc.text(word, x, y);
          x += wordWidth;

          if (i !== words.length - 1) {
            x += spaceWidth;
          }
        }
      }

      return y + lineHeight;
    }

   //Clean unwanted color tags, markdown headers, and encoding artifacts
    let cleanedResponse = responseData
      // Remove color labels like "Red*", "Yellow*", "Green*" (case-insensitive)
      .replace(/\b(Red|Yellow|Green)\s*\*/gi, "")
      // Remove any Ø=...* or encoding artifacts
      .replace(/Ø[^A-Za-z0-9\n\r]{0,10}\s*(Red|Yellow|Green)?\*/gi, "")
      .replace(/Ø=.*?\*/g, "")
      // Remove markdown headers (###, #### etc.)
      .replace(/#+\s*/g, "")
      // Remove asterisks used for emphasis (e.g. *word*)
      .replace(/\*/g, "")
      // Remove any stray non-ASCII garbage
      .replace(/[^\x00-\x7F]+/g, " ")

      .trim();

    // Now split cleaned text into lines
    const lines = cleanedResponse.split("\n");



    lines.forEach((line) => {
      let trimmedLine = line.trim();

      if (/^####\s*/.test(trimmedLine)) {
        const text = trimmedLine.replace(/^####\s*/, "");
        y = drawWrappedText(text, margin, y, "bold", baseFontSize + 4);
      } else if (/^###\s*/.test(trimmedLine)) {
        const text = trimmedLine.replace(/^###\s*/, "");
        y = drawWrappedText(text, margin, y, "bold", baseFontSize + 3);
      } else if (/^##\s*/.test(trimmedLine)) {
        const text = trimmedLine.replace(/^##\s*/, "");
        y = drawWrappedText(text, margin, y, "bold", baseFontSize + 2);
      } else {
        const chunks = parseInlineStyles(trimmedLine);
        y = drawStyledLine(y, margin, margin + maxLineWidth, chunks, baseFontSize, lineHeight);
      }
    });

    doc.save("Risk_Analysis.pdf");
  };

  return (
    <div className="uwfraud-page">
      <div className="heading-divfraud">
        <p className="text5">Financial Risk Intelligence</p>
      </div>

      <div
        className="uwfraud-search-container"
        style={{ display: "flex", alignItems: "center" }}
      >
        <Input
          ref={inputRef}
          placeholder="Please enter entity details like person name, organisation..."
          value={inputValue}
          onChange={onChangeHandler}
          className="uwfraud-input"
          bordered={false}
          style={{ flex: 1 }}
          suffix={inputValue ? (
            <CloseCircleOutlined
              onClick={() => {
                setInputValue("");
                setInputError("");
              }}
              style={{ cursor: "pointer", color: "#9CA3AF" }}
            />
          ) : null}
        />


        {inputError && (
          <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>{inputError}</div>
        )}

        <Button
          className="uwfraud-search-btn"
          onClick={handleSubmit}
          loading={loading}
          style={{ marginLeft: 8 }}
        >
          Submit
        </Button>
      </div>

      <div className="download-button-wrapper">
        <div className="response-text">Analysis</div>
        <Tooltip title="Download PDF">
          <Button className="download-pdf-btn" onClick={handleDownloadPDF}>
            <DownloadOutlined />
          </Button>
        </Tooltip>
      </div>

      <Card
        className="uwfraud-response-card"
        bodyStyle={{
          fontFamily: "'Inter', sans-serif",
          whiteSpace: "pre-wrap",
          overflowY: "auto",
          padding: "12px",
          height: "100%",
          scrollbarWidth: "thin",
        }}
      >
        {responseData ? <ReactMarkdown>{responseData}</ReactMarkdown> : ""}
      </Card>
    </div>
  );
}
